import React, { useState } from 'react';

import { useSnackbar } from 'notistack';

import { Button, FormControl, InputLabel, ListItemText, Menu, MenuItem, Select, Stack } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import { usePreferredOptionContext, usePricingOptionNamesContext } from './PricingToolSessionContext';
import { PRICING_OPTION_LABELS, optionLabel } from './constants';
import { exportCommercialApprovalHtml } from './export/exportCommercialApprovalHtml';
import { exportCommercialApprovalPdf } from './export/exportCommercialApprovalPdf';
import { useCommercialApprovalData } from './export/useCommercialApprovalData';
import type { PreferredOption } from './types';

export default function PricingToolToolbarActions() {
  const { preferredOption, setPreferredOption } = usePreferredOptionContext();
  const { optionNames } = usePricingOptionNamesContext();
  const commercialApprovalData = useCommercialApprovalData();
  const { enqueueSnackbar } = useSnackbar();

  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(null);
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const handlePreferredOptionChange = (event: SelectChangeEvent<string>) => {
    const value = event.target.value;
    setPreferredOption(value === '' ? null : (Number(value) as PreferredOption));
  };

  const requireCommercialApprovalData = () => {
    if (!commercialApprovalData) {
      enqueueSnackbar('Select a preferred option with package pricing before exporting.', { variant: 'warning' });
      return null;
    }
    return commercialApprovalData;
  };

  const handleExportHtml = () => {
    setExportMenuAnchor(null);
    const data = requireCommercialApprovalData();
    if (data) {
      exportCommercialApprovalHtml(data);
    }
  };

  const handleExportPdf = async () => {
    setExportMenuAnchor(null);
    const data = requireCommercialApprovalData();
    if (!data) {
      return;
    }
    setIsExportingPdf(true);
    try {
      await exportCommercialApprovalPdf(data);
    } catch {
      enqueueSnackbar('Failed to generate the Commercial Approval PDF.', { variant: 'error' });
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleExportAll = () => {
    setExportMenuAnchor(null);
    window.print();
  };

  return (
    <Stack alignItems="center" direction="row" justifyContent="flex-end" spacing={1}>
      <FormControl size="small" sx={{ minWidth: 160 }}>
        <InputLabel id="preferred-option-label">Preferred option</InputLabel>
        <Select
          label="Preferred option"
          labelId="preferred-option-label"
          onChange={handlePreferredOptionChange}
          value={preferredOption !== null ? String(preferredOption) : ''}
        >
          <MenuItem value="">None</MenuItem>
          {PRICING_OPTION_LABELS.map((_, index) => (
            <MenuItem key={index} value={String(index + 1)}>
              {optionLabel(optionNames, index)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Button disabled size="small" variant="outlined">
        Load Session
      </Button>
      <Button disabled size="small" variant="outlined">
        Save Session
      </Button>
      <Button
        disabled={isExportingPdf}
        onClick={(event) => setExportMenuAnchor(event.currentTarget)}
        size="small"
        variant="contained"
      >
        Export
      </Button>
      <Menu anchorEl={exportMenuAnchor} onClose={() => setExportMenuAnchor(null)} open={Boolean(exportMenuAnchor)}>
        <MenuItem onClick={handleExportPdf}>
          <ListItemText primary="Commercial Approval Export (PDF)" />
        </MenuItem>
        <MenuItem onClick={handleExportHtml}>
          <ListItemText primary="Commercial Approval HTML" />
        </MenuItem>
        <MenuItem onClick={handleExportAll}>
          <ListItemText primary="Export All" />
        </MenuItem>
      </Menu>
    </Stack>
  );
}
